import { TextField, makeStyles } from '@material-ui/core';
import styles from 'assets/jss/material-dashboard-pro-react/views/billingPaymentTrack/downloadZipDialogStyle';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import Button from 'components/CustomButtons/Button';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { translate } from 'components/Translation';
import { BodyM } from 'components/Typography/Typography';
import moment from 'moment';
import React, { useState } from 'react';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { dispatch } from 'redux/store';
import { finalize } from 'rxjs/operators';
import { BillsService } from 'services/bills/bills';
import { BillingRequest, BillingStatus } from 'utils/api/models/bills/bills';
import DownloadZipTable from './subcomponents/DownloadZipTable';
import { MONTH_YEAR, MON_YEAR, YEAR_MONTH } from 'views/Bills/constants';
import { now } from 'utils/date/getDate';
import Datetime from 'react-datetime';
import { dark_grey } from 'assets/jss/material-dashboard-pro-react';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { useSelector } from 'react-redux';
import { getBOMLabels } from 'redux/modules/dynamicLabels/selectors';
import { setDataLayer } from 'commons/gtm/dataLayerWrapper';
import { getLanguage } from 'utils/language/language';
import { upIdSelector } from 'redux/modules/session/selectors';

//@ts-ignore
const useStyles = makeStyles(styles);

interface IDownloadZipDialogProps {
	isOpen: boolean;
	hideDialog: () => void;
	setBillingRequestSuccessDialog: React.Dispatch<React.SetStateAction<boolean>>;
	setBillingRequest: React.Dispatch<React.SetStateAction<BillingRequest[]>>;
	billingRequest: BillingRequest[];
}

const DownloadZipDialog = ({
	isOpen,
	hideDialog,
	setBillingRequest,
	setBillingRequestSuccessDialog,
	billingRequest,
}: IDownloadZipDialogProps) => {
	const [openDatePicker, setOpenDatePicker] = useState(false);
	const [billPeriod, setBillPeriod] = useState('');
	const [billPeriodPayload, setBillPeriodForPayload] = useState('');

	const classes = useStyles();
	const labels = useSelector(getBOMLabels);
	const accId = useSelector(upIdSelector);

	const zipDialogLabels = labels?.zipDialogLabels || {};
	const searchingLimit = moment().subtract(zipDialogLabels.searchingLimit, 'months');
	moment.locale('en', {
		week: {
			dow: 0,
		},
	});
	const handleSubmit = () => {
		setDataLayer('download', getLanguage(), null, accId, null, {
			type: 'bills',
			billMonth: billPeriod,
		});
		dispatch(showLoadingIndicator());
		BillsService.createBillingRequest({ period: billPeriodPayload })
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe((response: any) => {
				hideDialog();
				setBillingRequestSuccessDialog(true);
				setBillingRequest([response?.response]);
			});
	};

	const dialogBody = (
		<>
			<hr></hr>
			<div className={classes.container}>
				<BodyM>{zipDialogLabels.selectMonth}</BodyM>
				<div className={classes.billPeriodSelectorWrapper}>
					<div className={classes.dateInputWrapper}>
						<TextField
							onChange={() => {
								setBillPeriod('');
							}}
							onFocus={() => {
								setOpenDatePicker(true);
							}}
							value={billPeriod}
							data-testid="bill-period"
							id="bill-period"
						/>
						<DateRangeIcon htmlColor={dark_grey} />
					</div>
					<Datetime
						closeOnSelect={true}
						renderDay={(props, currentDate) => (
							<td {...props}>
								<div className={classes.cell}>{currentDate.date()}</div>
							</td>
						)}
						open={true}
						onChange={(e: any) => {
							setOpenDatePicker(false);
							setBillPeriod(e.format(MONTH_YEAR));
							setBillPeriodForPayload(e.format(YEAR_MONTH));
						}}
						timeFormat={false}
						viewMode={'months'}
						dateFormat={MON_YEAR}
						isValidDate={(currentDate) => {
							return currentDate.isAfter(searchingLimit, 'months') && currentDate.isSameOrBefore(now);
						}}
					/>
				</div>
			</div>
			<ConditionalRender
				show={!!billingRequest && billingRequest.length > 0}
				onTrue={() => (
					<div className={classes.section}>
						<BodyM>{translate('payments.bills.ordered.zips')}</BodyM>
						<DownloadZipTable data={billingRequest} />
					</div>
				)}
			/>
			<hr />
			<div className={classes.buttonContainer}>
				<Button color="secondary-light" onClick={hideDialog} data-testId="cancel">
					{zipDialogLabels.cancelButton}
				</Button>
				<Button color="primary-light" onClick={handleSubmit} disabled={billPeriod === ''} data-testId="email">
					{zipDialogLabels.emailButton}
				</Button>
			</div>
		</>
	);

	return (
		<CustomDialog header={zipDialogLabels.header} isOpen={isOpen} hideDialog={hideDialog} dialogBody={dialogBody} />
	);
};

export default DownloadZipDialog;
